import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Upload, X, Video, Image as ImageIcon, Youtube } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const MediaUpload = ({ currentMedia, onMediaChange }) => {
  const [uploading, setUploading] = useState(false);
  const [mediaType, setMediaType] = useState(currentMedia?.type || 'none');
  const [youtubeUrl, setYoutubeUrl] = useState(currentMedia?.type === 'youtube' ? currentMedia.url : '');
  const [uploadedMedia, setUploadedMedia] = useState(
    currentMedia?.type === 'image' || currentMedia?.type === 'video' ? currentMedia : null
  );

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/flash-info-media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const media = {
        type: response.data.type,
        url: response.data.url
      };

      setUploadedMedia(media);
      setMediaType(response.data.type);
      onMediaChange(media);
      toast.success('Média uploadé avec succès');
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleYoutubeSubmit = () => {
    if (!youtubeUrl) {
      toast.error('Veuillez entrer une URL YouTube/Vimeo');
      return;
    }

    const isValid = youtubeUrl.includes('youtube.com') || 
                    youtubeUrl.includes('youtu.be') || 
                    youtubeUrl.includes('vimeo.com');

    if (!isValid) {
      toast.error('URL YouTube/Vimeo invalide');
      return;
    }

    const media = {
      type: 'youtube',
      url: youtubeUrl
    };

    onMediaChange(media);
    toast.success('Lien vidéo ajouté');
  };

  const handleRemoveMedia = () => {
    setUploadedMedia(null);
    setMediaType('none');
    setYoutubeUrl('');
    onMediaChange({ type: 'none', url: null });
    toast.success('Média supprimé');
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Type de média</Label>
        <RadioGroup value={mediaType} onValueChange={setMediaType} className="flex flex-col space-y-2 mt-2">
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="none" id="none" />
            <Label htmlFor="none" className="font-normal cursor-pointer">Aucun</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="image" id="image" />
            <Label htmlFor="image" className="font-normal cursor-pointer">Image</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="video" id="video" />
            <Label htmlFor="video" className="font-normal cursor-pointer">Vidéo (upload)</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="youtube" id="youtube" />
            <Label htmlFor="youtube" className="font-normal cursor-pointer">YouTube/Vimeo</Label>
          </div>
        </RadioGroup>
      </div>

      {(mediaType === 'image' || mediaType === 'video') && !uploadedMedia && (
        <div>
          <input
            type="file"
            id="media-upload"
            accept={mediaType === 'image' ? 'image/*' : 'video/*'}
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
          <label htmlFor="media-upload">
            <Button
              type="button"
              variant="outline"
              disabled={uploading}
              onClick={() => document.getElementById('media-upload').click()}
              className="w-full"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Upload en cours...' : `Upload ${mediaType === 'image' ? 'Image' : 'Vidéo'}`}
            </Button>
          </label>
        </div>
      )}

      {mediaType === 'youtube' && (
        <div className="space-y-2">
          <Label htmlFor="youtube-url">URL YouTube/Vimeo</Label>
          <div className="flex gap-2">
            <Input
              id="youtube-url"
              type="url"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
            />
            <Button type="button" onClick={handleYoutubeSubmit}>
              Ajouter
            </Button>
          </div>
        </div>
      )}

      {uploadedMedia && uploadedMedia.url && (
        <div className="relative">
          {uploadedMedia.type === 'image' && (
            <div className="relative">
              <img
                src={`${BACKEND_URL}${uploadedMedia.url}`}
                alt="Preview"
                className="w-full h-48 object-cover rounded-lg border"
              />
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={handleRemoveMedia}
                className="absolute top-2 right-2"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
          {uploadedMedia.type === 'video' && (
            <div className="relative">
              <video
                src={`${BACKEND_URL}${uploadedMedia.url}`}
                controls
                className="w-full h-48 rounded-lg border"
              />
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={handleRemoveMedia}
                className="absolute top-2 right-2"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {mediaType === 'youtube' && youtubeUrl && (
        <div className="relative p-4 border rounded-lg bg-gray-50">
          <div className="flex items-center gap-2 text-sm">
            <Youtube className="w-4 h-4 text-red-600" />
            <span className="truncate">{youtubeUrl}</span>
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={handleRemoveMedia}
            className="absolute top-2 right-2"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default MediaUpload;
